import { effectiveMissionTrigger } from "../missionTrigger.js";
import { mergeModelTestStep, modelTestStepForJobStatus, parseModelTestStepsJson, } from "../modelSkillTest.js";
import { CURRENTS_GRAPHQL } from "./queries.js";
import { currentsGraphQLFetch, CurrentsGraphQLError } from "./httpClient.js";
function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
export const CurrentsAPI = {
    async listAgents(cfg, orgId) {
        const conn = await currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.listOceanAgents,
            variables: { orgId, limit: 100 },
            resultKey: "listOceanAgents",
        });
        return conn.items;
    },
    async getAgent(cfg, agentId, orgId) {
        return currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.getOceanAgent,
            variables: { agentId, orgId },
            resultKey: "getOceanAgent",
        });
    },
    async createAgent(cfg, input) {
        return currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.createOceanAgent,
            variables: { input },
            resultKey: "createOceanAgent",
        });
    },
    async updateAgent(cfg, agentId, input, orgId) {
        return currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.updateOceanAgent,
            variables: { agentId, orgId, input },
            resultKey: "updateOceanAgent",
        });
    },
    async deleteAgent(cfg, agentId, orgId) {
        await currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.deleteOceanAgent,
            variables: { agentId, orgId },
            resultKey: "deleteOceanAgent",
        });
    },
    async invokeAgent(cfg, agentId, goal, orgId, contextJson, userQuestion) {
        const trimmedGoal = goal.trim();
        return currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.invokeOceanAgent,
            variables: {
                input: {
                    agentId,
                    orgId,
                    goal: trimmedGoal,
                    userQuestion: userQuestion?.trim() || trimmedGoal,
                    contextJson,
                },
            },
            resultKey: "invokeOceanAgent",
        });
    },
    async getJob(cfg, jobId) {
        return currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.getAIJob,
            variables: { jobId },
            resultKey: "getAIJob",
        });
    },
    async listMissionSteps(cfg, jobId) {
        const conn = await currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.listOceanMissionSteps,
            variables: { jobId, limit: 100 },
            resultKey: "listOceanMissionSteps",
        });
        return conn.items;
    },
    async listMissions(cfg, options) {
        const triggerFilter = options?.triggerType?.trim().toUpperCase() || "";
        const conn = await currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.listOceanMissions,
            variables: {
                from: options?.from,
                to: options?.to,
                orgId: options?.orgId,
                agentId: options?.agentId,
                appId: options?.appId,
                status: options?.status,
                limit: options?.limit ?? 100,
            },
            resultKey: "listOceanMissions",
        });
        if (!triggerFilter)
            return conn.items;
        return conn.items.filter((m) => effectiveMissionTrigger(m).triggerType === triggerFilter);
    },
    async getObsInsights(cfg, from, to, groupBy, orgId, appId) {
        return currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.getOceanObsInsights,
            variables: { from, to, orgId, appId, groupBy },
            resultKey: "getOceanObsInsights",
        });
    },
    async getOrgSkill(cfg, skillId, orgId) {
        return currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.getOceanOrgSkill,
            variables: { skillId, orgId },
            resultKey: "getOceanOrgSkill",
        });
    },
    async updateOrgSkill(cfg, skillId, input, orgId) {
        return currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.updateOceanOrgSkill,
            variables: { skillId, orgId, input },
            resultKey: "updateOceanOrgSkill",
        });
    },
    async deleteOrgSkill(cfg, skillId, orgId) {
        await currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.deleteOceanOrgSkill,
            variables: { skillId, orgId },
            resultKey: "deleteOceanOrgSkill",
        });
    },
    async createOrgSkill(cfg, input) {
        return currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.createOceanOrgSkill,
            variables: { input },
            resultKey: "createOceanOrgSkill",
        });
    },
    async generateSkillScript(cfg, skillId, description, orgId, options) {
        return currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.generateOceanSkillScript,
            variables: {
                input: {
                    skillId,
                    orgId,
                    description,
                    currentScriptBody: options?.currentScriptBody,
                    feedback: options?.feedback,
                },
            },
            resultKey: "generateOceanSkillScript",
        });
    },
    async testSkillScript(cfg, skillId, sampleInputJson, orgId, draftScriptBody, prefetchedDataJson) {
        return currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.testOceanSkillScript,
            variables: {
                input: { skillId, orgId, sampleInputJson, draftScriptBody, prefetchedDataJson },
            },
            resultKey: "testOceanSkillScript",
        });
    },
    async testModelSkill(cfg, skillId, sampleParamsJson, orgId, options) {
        const started = Date.now();
        let steps = [];
        const emit = (step) => {
            steps = mergeModelTestStep(steps, step);
            const merged = steps.find((s) => s.id === step.id) ?? step;
            options?.onStep?.(merged);
        };
        emit({ id: "client_start", label: "Invio mutation testOceanModelSkill", status: "running" });
        const initial = await currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.testOceanModelSkill,
            variables: {
                input: { skillId, orgId, sampleParamsJson },
            },
            resultKey: "testOceanModelSkill",
        });
        for (const s of parseModelTestStepsJson(initial.stepsJson)) {
            emit(s);
        }
        emit({ id: "client_start", label: "Mutation completata", status: "ok" });
        if (initial.errorMessage || initial.s3Key || !initial.jobId) {
            if (initial.errorMessage) {
                emit({ id: "test_failed", label: "Test terminato con errore", status: "error", detail: initial.errorMessage });
            }
            return {
                ...initial,
                steps,
                durationMs: initial.durationMs || Date.now() - started,
            };
        }
        const pollTimeout = options?.pollTimeoutMs ?? 180_000;
        const pollInterval = options?.pollIntervalMs ?? 2_000;
        let lastStatus = null;
        let pollAttempts = 0;
        while (Date.now() - started < pollTimeout) {
            await sleep(pollInterval);
            pollAttempts += 1;
            let job = null;
            try {
                job = await this.getJob(cfg, initial.jobId);
            }
            catch (e) {
                if (e instanceof CurrentsGraphQLError && /not found/i.test(e.message)) {
                    emit({
                        id: "job_poll",
                        label: "Poll getAIJob — job non ancora visibile",
                        status: "running",
                        detail: `tentativo ${pollAttempts}`,
                    });
                    continue;
                }
                throw e;
            }
            if (!job)
                continue;
            if (job.status !== lastStatus) {
                emit(modelTestStepForJobStatus(job.status, job.progress));
                lastStatus = job.status;
                emit({
                    id: "job_poll",
                    label: "Poll getAIJob — job trovato",
                    status: "ok",
                    detail: `${job.status}${job.progress != null ? ` · ${job.progress}%` : ""}`,
                });
            }
            if (job.status === "COMPLETED") {
                emit({
                    id: "output_asset",
                    label: "Verifica outputAssetKey su S3",
                    status: job.outputAssetKey ? "ok" : "error",
                    detail: job.outputAssetKey || "outputAssetKey mancante",
                });
                if (job.outputAssetMime) {
                    emit({
                        id: "output_mime",
                        label: "MIME output registrato",
                        status: "ok",
                        detail: job.outputAssetMime,
                    });
                }
                emit({
                    id: "image_published",
                    label: "Immagine finale pubblicata",
                    status: job.outputAssetKey ? "ok" : "error",
                    detail: job.outputAssetKey ?? undefined,
                });
                return {
                    prompt: initial.prompt,
                    modelId: initial.modelId,
                    jobId: initial.jobId,
                    status: job.status,
                    s3Key: job.outputAssetKey ?? null,
                    mime: job.outputAssetMime ?? null,
                    durationMs: Date.now() - started,
                    costCents: job.costCents,
                    steps,
                    stepsJson: initial.stepsJson,
                };
            }
            if (job.status === "FAILED" || job.status === "CANCELLED" || job.status === "TIMED_OUT") {
                emit({
                    id: "test_failed",
                    label: "Generazione fallita",
                    status: "error",
                    detail: job.errorMessage ?? job.status,
                });
                return {
                    prompt: initial.prompt,
                    modelId: initial.modelId,
                    jobId: initial.jobId,
                    status: job.status,
                    durationMs: Date.now() - started,
                    costCents: job.costCents,
                    errorMessage: job.errorMessage ?? `Job ${job.status}`,
                    steps,
                    stepsJson: initial.stepsJson,
                };
            }
        }
        emit({
            id: "test_failed",
            label: "Timeout poll provider",
            status: "error",
            detail: "180s",
        });
        return {
            prompt: initial.prompt,
            modelId: initial.modelId,
            jobId: initial.jobId,
            status: "TIMED_OUT",
            durationMs: Date.now() - started,
            errorMessage: "Timeout in attesa del sub-job provider (180s)",
            steps,
            stepsJson: initial.stepsJson,
        };
    },
    async publishSkillScript(cfg, skillId, orgId) {
        return currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.publishOceanSkillScript,
            variables: { input: { skillId, orgId } },
            resultKey: "publishOceanSkillScript",
        });
    },
    async listOrgSkills(cfg, orgId, onlyEnabled = true) {
        const conn = await currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.listOceanOrgSkills,
            variables: { orgId, limit: 100 },
            resultKey: "listOceanOrgSkills",
        });
        return onlyEnabled ? conn.items.filter((s) => s.enabled) : conn.items;
    },
    async listOrgMemories(cfg, orgId, onlyEnabled = true) {
        const conn = await currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.listOceanOrgMemories,
            variables: { orgId, limit: 100 },
            resultKey: "listOceanOrgMemories",
        });
        return onlyEnabled ? conn.items.filter((m) => m.enabled) : conn.items;
    },
    async listOrgGuardrails(cfg, orgId, onlyEnabled = true) {
        const conn = await currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.listOceanOrgGuardrails,
            variables: { orgId, limit: 100 },
            resultKey: "listOceanOrgGuardrails",
        });
        return onlyEnabled ? conn.items.filter((g) => g.enabled) : conn.items;
    },
    async listBedrockModels(cfg) {
        const conn = await currentsGraphQLFetch({
            config: cfg,
            query: CURRENTS_GRAPHQL.listOceanBedrockModels,
            variables: { limit: 300 },
            resultKey: "listOceanBedrockModels",
        });
        return (conn.items ?? [])
            .filter((m) => m.enabled !== false)
            .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, "it"));
    },
};
